import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { CButton, CForm, CAlert } from "@coreui/react";
import { useNavigate } from "react-router-dom";
import openEyeIcon from "../../assets/Images/eye-open.png";
import closedEyeIcon from "../../assets/Images/eye-closed.png";
import logoImg from "../../assets/Images/White_Logo.svg";
import * as actions from "../../store/actions/index";
import { SOCKET_BASE_URL } from "../Helpers/helpers";

// ─── OTP / Registration constants ────────────────────────────────────────────
const OTP_LENGTH = 6;
const OTP_EXPIRY_SECONDS = 5 * 60; // 5 minutes
const RESEND_COOLDOWN_SECONDS = 30;
const MAX_OTP_ATTEMPTS = 5;

// Registration steps
const STEP_FORM = "FORM";
const STEP_OTP = "OTP";
const STEP_DONE = "DONE";

// ─── API helpers ─────────────────────────────────────────────────────────────
const apiRequest = async (path, body) => {
    let response;
    try {
        response = await fetch(`${SOCKET_BASE_URL}${path}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    } catch (networkErr) {
        throw new ApiError(
            "Network error. Please check your connection and try again.",
            0,
            networkErr,
        );
    }

    let data = null;
    try {
        data = await response.json();
    } catch {
    }

    if (!response.ok) {
        const message =
            data?.message ||
            data?.error ||
            defaultMessageForStatus(response.status);
        throw new ApiError(message, response.status, data);
    }

    return data;
};

class ApiError extends Error {
    constructor(message, status, payload) {
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.payload = payload;
    }
}

const defaultMessageForStatus = (status) => {
    switch (status) {
        case 400:
            return "Invalid request. Please check the details you entered.";
        case 401:
            return "Session expired. Please try again.";
        case 409:
            return "An account with this email already exists.";
        case 422:
            return "The information provided is invalid.";
        case 429:
            return "Too many attempts. Please wait before trying again.";
        case 500:
        case 502:
        case 503:
            return "Server error. Please try again in a moment.";
        default:
            return "Something went wrong. Please try again.";
    }
};

const registerApi = {
  register: (payload) =>
    apiRequest("/api/v1/user/register", payload),

  verifyOtp: (payload) =>
    apiRequest(
      "/api/v1/user/auth/verify-email-otp",
      payload
    ),

  resendOtp: (payload) =>
    apiRequest(
      "/api/v1/user/auth/resend-email-otp",
      payload
    ),
};

// ─── Validation helpers ──────────────────────────────────────────────────────
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Success criteria: at least 8 characters, 1 uppercase, 1 number, 1 special character.
const PASSWORD_RE = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
const PASSWORD_EXAMPLE = "Secur3!";

const validateForm = ({ firstName, lastName, email, password, confirmPassword }) => {
    const errors = {};
    if (!firstName || firstName.trim().length < 2) {
        errors.firstName = "Please enter your first name.";
    }
    if (!lastName || lastName.trim().length < 2) {
        errors.lastName = "Please enter your last name.";
    }
    if (!email || !EMAIL_RE.test(email)) {
        errors.email = "Please enter a valid email address.";
    }
    if (!password || !PASSWORD_RE.test(password)) {
        errors.password = "Min 8 chars, 1 uppercase, 1 number, 1 special character.";
    }
    if (confirmPassword !== password) {
        errors.confirmPassword = "Passwords do not match.";
    }
    return errors;
};

// ─── Password strength ───────────────────────────────────────────────────────
// Returns null for an empty password, otherwise a label/percent/color for the meter.
const getPasswordStrength = (pwd) => {
    if (!pwd) return null;

    if (pwd.length < 8) {
        return { label: "Too short", percent: 15, color: "#e55353" };
    }

    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[a-z]/.test(pwd)) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;

    if (score <= 2) return { label: "Weak", percent: 32, color: "#e55353" };
    if (score === 3) return { label: "Fair", percent: 56, color: "#f0a020" };
    if (score === 4) return { label: "Good", percent: 78, color: "#2f78c4" };
    return { label: "Strong", percent: 100, color: "#22b573" };
};

// ─── Component ────────────────────────────────────────────────────────────────
const RegisterNew = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [step, setStep] = useState(STEP_FORM);

    // Browser tab title for this page.
    useEffect(() => {
        document.title = "Create an Account - QuickQuery";
    }, []);

    // Form fields
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmVisible, setConfirmVisible] = useState(false);

    const [fieldErrors, setFieldErrors] = useState({});
    const [validated, setValidated] = useState(false);

    // Alert / feedback state
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    const [loading, setLoading] = useState(false);

    // OTP state (sent to email)
    const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
    const [otpExpiresAt, setOtpExpiresAt] = useState(null);
    const [otpRemainingSeconds, setOtpRemainingSeconds] = useState(0);
    const [otpAttemptsLeft, setOtpAttemptsLeft] = useState(MAX_OTP_ATTEMPTS);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [resendLoading, setResendLoading] = useState(false);
    const otpInputRefs = useRef([]);

    // ── OTP expiry countdown ──────────────────────────────────────────────────
    useEffect(() => {
        if (step !== STEP_OTP || !otpExpiresAt) return;

        const tick = () => {
            const secs = Math.max(0, Math.ceil((otpExpiresAt - Date.now()) / 1000));
            setOtpRemainingSeconds(secs);
            if (secs <= 0) {
                setShowError(true);
                setErrorMessage("Your OTP has expired. Please request a new code.");
            }
        };

        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [step, otpExpiresAt]);

    // ── Resend cooldown countdown ─────────────────────────────────────────────
    useEffect(() => {
        if (resendCooldown <= 0) return;
        const interval = setInterval(() => {
            setResendCooldown((s) => Math.max(0, s - 1));
        }, 1000);
        return () => clearInterval(interval);
    }, [resendCooldown]);

    const formatCountdown = (secs) => {
        const m = Math.floor(secs / 60).toString().padStart(2, "0");
        const s = (secs % 60).toString().padStart(2, "0");
        return `${m}:${s}`;
    };

    // ── Field change handlers ─────────────────────────────────────────────────
    const clearFieldError = (field) => {
        setFieldErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
        setShowError(false);
    };

    const handleFirstNameChange = (e) => {
        setFirstName(e.target.value);
        clearFieldError("firstName");
    };
    const handleLastNameChange = (e) => {
        setLastName(e.target.value);
        clearFieldError("lastName");
    };
    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        clearFieldError("email");
    };
    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        clearFieldError("password");
        // Re-check the match as soon as both fields have values.
        if (confirmPassword) clearFieldError("confirmPassword");
    };
    const handleConfirmPasswordChange = (e) => {
        setConfirmPassword(e.target.value);
        clearFieldError("confirmPassword");
    };

    const handleCancel = () => {
        setFirstName("");
        setLastName("");
        setEmail("");
        setPassword("");
        setConfirmPassword("");
        setFieldErrors({});
        setValidated(false);
        setShowError(false);
        setShowSuccess(false);
    };

    // All fields must have a value before the submit button is enabled.
    const allFieldsFilled =
        firstName.trim() !== "" &&
        lastName.trim() !== "" &&
        email.trim() !== "" &&
        password !== "" &&
        confirmPassword !== "";

    const passwordStrength = getPasswordStrength(password);
    const isPasswordValid = PASSWORD_RE.test(password);

    // ── Submit registration form ──────────────────────────────────────────────
    const handleRegisterSubmit = async (event) => {
        event.preventDefault();

        const errors = validateForm({
            firstName,
            lastName,
            email,
            password,
            confirmPassword,
        });

        setFieldErrors(errors);
        setValidated(true);

        if (Object.keys(errors).length > 0) {
            return;
        }

        setLoading(true);
        setShowError(false);
        setErrorMessage("");

        try {
            const payload = {
                FirstName: firstName.trim(),
                LastName: lastName.trim(),
                EmailAddress: email.trim().toLowerCase(),
                Password: password,
                ConfirmPassword: confirmPassword,
            };

            const response = await registerApi.register(payload);

            if (response && response.success === true) {
                const registeredEmail = payload.EmailAddress;

                navigate(
                    `/verify-email?email=${encodeURIComponent(registeredEmail)}`,
                    { replace: true },
                );

                setStep(STEP_OTP);
                setOtpExpiresAt(Date.now() + OTP_EXPIRY_SECONDS * 1000);
                return;
            }

            setShowError(true);
            setErrorMessage(
                response?.message || "Registration failed. Please try again."
            );
        } catch (err) {
            setShowError(true);

            if (err instanceof ApiError) {
                if (err.status === 409) {
                    setFieldErrors({ email: "This email is already registered." });
                }
                setErrorMessage(err.message);
            } else {
                setErrorMessage("An unexpected error occurred. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    // ── OTP input handlers ────────────────────────────────────────────────────
    const handleOtpChange = (index, value) => {
        const digit = value.replace(/[^0-9]/g, "").slice(-1);
        const next = [...otp];
        next[index] = digit;
        setOtp(next);
        setShowError(false);

        if (digit && index < OTP_LENGTH - 1) {
            otpInputRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            otpInputRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
        if (!pasted) return;
        e.preventDefault();
        const next = Array(OTP_LENGTH).fill("");
        pasted
            .slice(0, OTP_LENGTH)
            .split("")
            .forEach((d, i) => (next[i] = d));
        setOtp(next);
        const lastIndex = Math.min(pasted.length, OTP_LENGTH) - 1;
        otpInputRefs.current[lastIndex]?.focus();
    };

    // ── Verify OTP (email code) ──────────────────────────────────────────────
    const handleVerifyOtp = async (event) => {
        event.preventDefault();

        if (otpRemainingSeconds <= 0) {
            setShowError(true);
            setErrorMessage("Your OTP has expired. Please request a new code.");
            return;
        }

        const code = otp.join("");
        if (code.length !== OTP_LENGTH) {
            setShowError(true);
            setErrorMessage(`Please enter the ${OTP_LENGTH}-digit code.`);
            return;
        }

        setLoading(true);
        setShowError(false);

        try {
            await registerApi.verifyOtp({
                email: email.trim().toLowerCase(),
                code,
            });

            try {
                await dispatch(actions.userLogin(email.trim().toLowerCase(), password));
            } catch {
                // Non-fatal: verification succeeded even if auto-login didn't.
            }

            setStep(STEP_DONE);
            setShowSuccess(true);
            setSuccessMessage("Your account has been verified successfully!");
            setTimeout(() => navigate("/query"), 1200);
        } catch (err) {
            setShowError(true);
            if (err instanceof ApiError) {
                if (err.status === 410) {
                    setOtpRemainingSeconds(0);
                    setErrorMessage("Your OTP has expired. Please request a new code.");
                } else {
                    const attemptsLeft = Math.max(0, otpAttemptsLeft - 1);
                    setOtpAttemptsLeft(attemptsLeft);
                    setErrorMessage(
                        attemptsLeft > 0
                            ? `${err.message} (${attemptsLeft} attempt${attemptsLeft !== 1 ? "s" : ""} left)`
                            : "Too many incorrect attempts. Please request a new code.",
                    );
                }
            } else {
                setErrorMessage("An unexpected error occurred. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    // ── Resend OTP (email code) ──────────────────────────────────────────────
    const handleResendOtp = async () => {
        if (resendCooldown > 0 || resendLoading) return;

        setResendLoading(true);
        setShowError(false);

        try {
            const data = await registerApi.resendOtp({
                email: email.trim().toLowerCase(),
            });

            const expiresInSeconds = data?.expiresIn ?? OTP_EXPIRY_SECONDS;
            setOtpExpiresAt(Date.now() + expiresInSeconds * 1000);
            setOtpAttemptsLeft(MAX_OTP_ATTEMPTS);
            setOtp(Array(OTP_LENGTH).fill(""));
            setResendCooldown(RESEND_COOLDOWN_SECONDS);
            setShowSuccess(true);
            setSuccessMessage("A new verification code has been emailed to you.");
            otpInputRefs.current[0]?.focus();
        } catch (err) {
            setShowError(true);
            setErrorMessage(
                err instanceof ApiError
                    ? err.message
                    : "Could not resend the code. Please try again.",
            );
        } finally {
            setResendLoading(false);
        }
    };

    const goBackToForm = () => {
        setStep(STEP_FORM);
        setShowError(false);
        setShowSuccess(false);
    };

    const eyeIcon = (visible) => (visible ? openEyeIcon : closedEyeIcon);

    return (
        <div className="qq-login-page">
            <style>{`
        .qq-login-page {
          min-height: 100vh;
          display: flex;
          font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }

        /* ───────── Left panel (shared brand panel) ───────── */
       .qq-left {
  flex: 1 1 50%;
  width: 50%;
  position: relative;

  background-color: #023349;
  background-image: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.06) 1px,
    transparent 1px
  );
  background-size: 22px 22px;

  color: #e7e8ee;

  padding: 56px 64px;

  display: flex;
  flex-direction: column;

  /* Center content vertically */
  justify-content: center;

  /* Keep content aligned to the left */
  align-items: flex-start;

  min-width: 0;
  box-sizing: border-box;
}

        .qq-brand {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .qq-brand-mark {
          width: 30px;
          height: 30px;
          border-radius: 8px;
          background: linear-gradient(135deg, #6c5ce7, #4b3fd1);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .qq-brand-mark svg { width: 16px; height: 16px; }

        .qq-brand-name {
          font-size: 15px;
          font-weight: 600;
          color: #f4f4f8;
          letter-spacing: 0.01em;
        }

        .qq-logo {
          height: 28px;
          width: auto;
          display: block;
        }

        .qq-version-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-top: 14px;
          padding: 5px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.09);
          font-size: 12px;
          color: #a7a9bd;
          width: fit-content;
        }

        .qq-version-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #35d07f;
        }

        .qq-headline {
          margin-top: 40px;
          font-size: 38px;
          line-height: 1.18;
          font-weight: 600;
          color: #f7f7fb;
          max-width: 460px;
          letter-spacing: -0.01em;
        }

        .qq-subtext {
          margin-top: 14px;
          font-size: 14.5px;
          line-height: 1.6;
          color: #9698ad;
          max-width: 420px;
        }

        .qq-card {
          margin-top: 36px;
          background: rgba(255,255,255,0.035);
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 14px;
          padding: 18px 20px;
          max-width: 440px;
        }

        .qq-card-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 13px;
          color: #c7c8d8;
          font-weight: 500;
        }

        .qq-card-head-left { display: flex; align-items: center; gap: 8px; }

        .qq-online-badge {
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.03em;
          color: #35d07f;
          background: rgba(53,208,127,0.12);
          padding: 3px 9px;
          border-radius: 999px;
        }

        .qq-card-stats { display: flex; gap: 36px; margin-top: 16px; }
        .qq-card-stat-label { font-size: 10.5px; color: #7c7e93; letter-spacing: 0.02em; }
        .qq-card-stat-value { margin-top: 4px; font-size: 19px; font-weight: 600; color: #f2f2f7; }
        .qq-card-stat-value.qq-green { color: #3fdc8e; }

        .qq-footer-stats { display: flex; gap: 48px; margin-top: 44px; }
        .qq-footer-stat-value { font-size: 21px; font-weight: 600; color: #f2f2f7; }
        .qq-footer-stat-label { margin-top: 4px; font-size: 11px; color: #7c7e93; letter-spacing: 0.01em; }

        /* ───────── Right panel ───────── */
        .qq-right {
          flex: 1 1 50%;
          width: 50%;
          background: #e4e4e7;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          min-width: 0;
          box-sizing: border-box;
        }

        .qq-auth-card {
          width: 100%;
          max-width: 420px;
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0 20px 45px rgba(20,20,35,0.10);
          padding: 24px 28px 20px;
        }

        .qq-auth-title { font-size: 20px; font-weight: 700; color: #16171f; margin: 0; }
        .qq-auth-subtitle { margin-top: 4px; font-size: 12.5px; color: #8b8d9c; line-height: 1.45; }

        .qq-fields-note {
          margin-top: 8px;
          font-size: 11px;
          color: #9799a8;
        }

        /* ── Plain, icon-free inputs (image 2 style) ── */
        .qq-field { margin-top: 12px; }
        .qq-field-row { display: flex; gap: 12px; margin-top: 12px; }
        .qq-field-row .qq-field { margin-top: 0; flex: 1; }

        .qq-field-label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: #2a2c3a;
          margin-bottom: 4px;
        }

        .qq-plain-input {
          width: 100%;
          box-sizing: border-box;
          padding: 8px 12px;
          font-size: 13.5px;
          border-radius: 8px;
          border: 1px solid #dcdde3;
          color: #23242f;
          outline: none;
          background: #fff;
        }

        .qq-plain-input::placeholder { color: #a7a9b8; }
        .qq-plain-input:focus { border-color: #7f74ea; box-shadow: 0 0 0 3px rgba(108,92,231,0.12); }
        .qq-plain-input.qq-invalid { border-color: #e55353; }

        .qq-password-wrap { position: relative; width: 100%; }
        .qq-password-wrap .qq-plain-input { padding-right: 66px; }
        .qq-eye-btn {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          cursor: pointer;
          width: 18px;
          opacity: 0.7;
        }

        .qq-check-badge {
          position: absolute;
          right: 36px;
          top: 50%;
          transform: translateY(-50%);
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #22b573;
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .qq-field-error { margin-top: 4px; font-size: 11px; color: #e55353; }
        .qq-field-hint { margin-top: 4px; font-size: 11px; line-height: 1.4; color: #8b8d9c; }
        .qq-field-hint strong { color: #5b5d6d; }

        /* ── Password strength meter ── */
        .qq-strength { margin-top: 6px; }
        .qq-strength-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 11.5px;
        }
        .qq-strength-label {
          color: #3b7ddd;
          text-underline-offset: 2px;
        }
        .qq-strength-value { font-weight: 700; }
        .qq-strength-track {
          margin-top: 5px;
          height: 5px;
          border-radius: 999px;
          background: #e5e7eb;
          overflow: hidden;
        }
        .qq-strength-fill {
          height: 100%;
          border-radius: 999px;
          transition: width 0.2s ease, background 0.2s ease;
        }

        .qq-terms-row {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          margin-top: 12px;
        }

        .qq-terms-row input[type="checkbox"] {
          margin-top: 3px;
          width: 15px;
          height: 15px;
          accent-color: #5b4fe0;
          flex-shrink: 0;
        }

        .qq-terms-text { font-size: 11.5px; line-height: 1.55; color: #8b8d9c; }
        .qq-terms-text a { color: #5b4fe0; font-weight: 600; text-decoration: none; }

        .qq-submit-btn {
          margin-top: 14px;
          width: 100%;
          background: #0095BD;
          border: none;
          border-radius: 9px;
          padding: 10px 0;
          font-size: 14px;
          font-weight: 600;
        }
        .qq-submit-btn:hover:not(:disabled) { background: #007A9A; }
        .qq-submit-btn:disabled {
          background: #e2e3e8;
          color: #9799a8;
          cursor: not-allowed;
        }

        .qq-cancel-btn {
          margin-top: 8px;
          width: 100%;
          background: #fff;
          border: 1px solid #e1e2e8;
          color: #4a4c5c;
          border-radius: 9px;
          padding: 10px 0;
          font-size: 14px;
          font-weight: 600;
        }
        .qq-cancel-btn:hover { background: #f6f6f8; }

        .qq-signup-row {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #eeeef2;
          text-align: center;
          font-size: 12.5px;
          color: #8b8d9c;
        }
        .qq-signup-link { color: #5b4fe0; font-weight: 600; cursor: pointer; text-decoration: none; }

        /* ── OTP step ── */
        .qq-otp-row { display: flex; gap: 8px; justify-content: center; margin-top: 14px; }
        .qq-otp-box {
          width: 42px;
          height: 50px;
          text-align: center;
          font-size: 1.2em;
          font-weight: 600;
          border-radius: 8px;
          border: 1px solid #dcdde3;
          outline: none;
        }
        .qq-otp-box:focus { border-color: #7f74ea; box-shadow: 0 0 0 3px rgba(108,92,231,0.12); }
        .qq-otp-meta { margin-top: 10px; text-align: center; font-size: 13px; color: #6b6d7d; }
        .qq-otp-link { color: #5b4fe0; font-weight: 600; cursor: pointer; }
        .qq-otp-muted { color: #a2a4b3; }

        /* Tablet: keep both halves, but tighten the left panel's copy/padding. */
        @media (max-width: 1080px) {
          .qq-left { padding: 40px 36px; }
          .qq-headline { font-size: 30px; max-width: none; }
          .qq-subtext { max-width: none; }
          .qq-footer-stats { gap: 28px; flex-wrap: wrap; }
        }

        /* Mobile: stack the two halves, each full width. */
        @media (max-width: 860px) {
          .qq-login-page { flex-direction: column; }
          .qq-left,
          .qq-right {
            width: 100%;
            flex: none;
          }
          .qq-left { padding: 32px 24px; }
          .qq-headline { font-size: 26px; }
          .qq-card { max-width: none; }
          .qq-footer-stats { margin-top: 28px; gap: 24px; }
          .qq-right { padding: 20px; }
          .qq-auth-card { max-width: none; padding: 22px 20px 18px; }
        }

        @media (max-width: 460px) {
          .qq-field-row { flex-direction: column; gap: 12px; }
          .qq-footer-stats { flex-wrap: wrap; row-gap: 16px; }
        }
      `}</style>

            {/* ───────── Left: brand / pitch panel ───────── */}
            <div className="qq-left">
                <div>
                    <div className="qq-brand">
                        <img src={logoImg} alt="QuickQuery" className="qq-logo" />
                    </div>

                    {/* <div className="qq-version-pill">
                        <span className="qq-version-dot" />
                        Version 1 Active
                    </div> */}

                    <h1 className="qq-headline">Automate your account mapping in minutes</h1>
                    <p className="qq-subtext">
                        Configure standard expense categories, run clean multi-factor
                        compliance reporting, and synchronize seamlessly to your general
                        ledger.
                    </p>

                    {/* <div className="qq-card">
                        <div className="qq-card-head">
                            <span className="qq-card-head-left">Active Integrations</span>
                            <span className="qq-online-badge">ONLINE</span>
                        </div>
                        <div className="qq-card-stats">
                            <div>
                                <div className="qq-card-stat-label">MAPPED ACCOUNTS</div>
                                <div className="qq-card-stat-value">14,204</div>
                            </div>
                            <div>
                                <div className="qq-card-stat-label">SYNC ACCURACY</div>
                                <div className="qq-card-stat-value qq-green">99.98%</div>
                            </div>
                        </div>
                    </div> */}
                </div>

                {/* <div className="qq-footer-stats">
                    <div>
                        <div className="qq-footer-stat-value">10k+</div>
                        <div className="qq-footer-stat-label">HAPPY ENTERPRISE TEAMS</div>
                    </div>
                    <div>
                        <div className="qq-footer-stat-value">$2.4B+</div>
                        <div className="qq-footer-stat-label">MAPPED TRANSACTIONS</div>
                    </div>
                    <div>
                        <div className="qq-footer-stat-value">12 mins</div>
                        <div className="qq-footer-stat-label">AVERAGE SETUP TIME</div>
                    </div>
                </div> */}
            </div>

            {/* ───────── Right: form panel ───────── */}
            <div className="qq-right">
                <div className="qq-auth-card">
                    {step === STEP_FORM && (
                        <>
                            <h2 className="qq-auth-title">Create your account</h2>
                            <p className="qq-auth-subtitle">
                                You will verify your email next, then create your first
                                workspace.
                            </p>

                            {showError && (
                                <CAlert color="warning" dismissible onClose={() => setShowError(false)} className="mt-3 mb-0">
                                    {errorMessage}
                                </CAlert>
                            )}
                            {showSuccess && (
                                <CAlert color="success" dismissible onClose={() => setShowSuccess(false)} className="mt-3 mb-0">
                                    {successMessage}
                                </CAlert>
                            )}

                            <CForm noValidate validated={validated} onSubmit={handleRegisterSubmit}>
                                {/* First / Last name */}
                                <div className="qq-field-row">
                                    <div className="qq-field">
                                        <label className="qq-field-label" htmlFor="FirstName">First Name</label>
                                        <input
                                            id="FirstName"
                                            type="text"
                                            className={`qq-plain-input${fieldErrors.firstName ? " qq-invalid" : ""}`}
                                            placeholder="e.g. Sarah"
                                            value={firstName}
                                            onChange={handleFirstNameChange}
                                        />
                                        {fieldErrors.firstName && <div className="qq-field-error">{fieldErrors.firstName}</div>}
                                    </div>
                                    <div className="qq-field">
                                        <label className="qq-field-label" htmlFor="LastName">Last Name</label>
                                        <input
                                            id="LastName"
                                            type="text"
                                            className={`qq-plain-input${fieldErrors.lastName ? " qq-invalid" : ""}`}
                                            placeholder="e.g. Connor"
                                            value={lastName}
                                            onChange={handleLastNameChange}
                                        />
                                        {fieldErrors.lastName && <div className="qq-field-error">{fieldErrors.lastName}</div>}
                                    </div>
                                </div>

                                {/* Email */}
                                <div className="qq-field">
                                    <label className="qq-field-label" htmlFor="EmailAddress">Email Address</label>
                                    <input
                                        id="EmailAddress"
                                        type="email"
                                        className={`qq-plain-input${fieldErrors.email ? " qq-invalid" : ""}`}
                                        placeholder="you@company.com"
                                        value={email}
                                        onChange={handleEmailChange}
                                    />
                                    {fieldErrors.email && <div className="qq-field-error">{fieldErrors.email}</div>}
                                </div>

                                {/* Password */}
                                <div className="qq-field">
                                    <label className="qq-field-label" htmlFor="Password">Password</label>
                                    <div className="qq-password-wrap">
                                        <input
                                            id="Password"
                                            type={passwordVisible ? "text" : "password"}
                                            className={`qq-plain-input${fieldErrors.password ? " qq-invalid" : ""}`}
                                            placeholder="At least 8 characters"
                                            value={password}
                                            onChange={handlePasswordChange}
                                        />
                                        {isPasswordValid && (
                                            <span className="qq-check-badge" aria-label="Password meets requirements">
                                                ✓
                                            </span>
                                        )}
                                        <img
                                            src={eyeIcon(passwordVisible)}
                                            alt=""
                                            className="qq-eye-btn"
                                            onClick={() => setPasswordVisible((v) => !v)}
                                        />
                                    </div>

                                    {/* Short suggestion / validation note — only takes a line when it's actually needed. */}
                                    {fieldErrors.password ? (
                                        <div className="qq-field-error">{fieldErrors.password}</div>
                                    ) : (
                                        <div className="qq-field-hint">
                                            <strong>Success criteria:</strong> 8+ chars, 1 uppercase, 1 number, 1 special char
                                            (e.g. <strong>{PASSWORD_EXAMPLE}</strong>).
                                        </div>
                                    )}

                                    {passwordStrength && (
                                        <div className="qq-strength">
                                            <div className="qq-strength-row">
                                                <span className="qq-strength-label">Password strength</span>
                                                <span
                                                    className="qq-strength-value"
                                                    style={{ color: passwordStrength.color }}
                                                >
                                                    {passwordStrength.label}
                                                </span>
                                            </div>
                                            <div className="qq-strength-track">
                                                <div
                                                    className="qq-strength-fill"
                                                    style={{
                                                        width: `${passwordStrength.percent}%`,
                                                        background: passwordStrength.color,
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Retype Password */}
                                <div className="qq-field">
                                    <label className="qq-field-label" htmlFor="ConfirmPassword">Retype Password</label>
                                    <div className="qq-password-wrap">
                                        <input
                                            id="ConfirmPassword"
                                            type={confirmVisible ? "text" : "password"}
                                            className={`qq-plain-input${fieldErrors.confirmPassword ? " qq-invalid" : ""}`}
                                            placeholder="Re-enter your password"
                                            value={confirmPassword}
                                            onChange={handleConfirmPasswordChange}
                                        />
                                        {confirmPassword && confirmPassword === password && (
                                            <span className="qq-check-badge" aria-label="Passwords match">
                                                ✓
                                            </span>
                                        )}
                                        <img
                                            src={eyeIcon(confirmVisible)}
                                            alt=""
                                            className="qq-eye-btn"
                                            onClick={() => setConfirmVisible((v) => !v)}
                                        />
                                    </div>
                                    {fieldErrors.confirmPassword && (
                                        <div className="qq-field-error">{fieldErrors.confirmPassword}</div>
                                    )}
                                </div>

                                <CButton
                                    type="submit"
                                    className="qq-submit-btn"
                                    disabled={loading || !allFieldsFilled}
                                >
                                    {loading ? "Please wait..." : "Create account"}
                                </CButton>

                                <div className="qq-fields-note">
                                    All fields are required before the account can be created.
                                </div>

                                <CButton type="button" className="qq-cancel-btn" onClick={handleCancel} disabled={loading}>
                                    Cancel
                                </CButton>
                            </CForm>

                            <div className="qq-signup-row">
                                Already have an account?{" "}
                                <span className="qq-signup-link" onClick={() => navigate("/login")}>
                                    Sign in
                                </span>
                            </div>
                        </>
                    )}

                    {step === STEP_OTP && (
                        <>
                            <h2 className="qq-auth-title">Verify your email</h2>
                            <p className="qq-auth-subtitle">
                                Enter the {OTP_LENGTH}-digit code emailed to <strong>{email}</strong>
                            </p>

                            {showError && (
                                <CAlert color="warning" dismissible onClose={() => setShowError(false)} className="mt-3 mb-0">
                                    {errorMessage}
                                </CAlert>
                            )}
                            {showSuccess && (
                                <CAlert color="success" dismissible onClose={() => setShowSuccess(false)} className="mt-3 mb-0">
                                    {successMessage}
                                </CAlert>
                            )}

                            <CForm noValidate onSubmit={handleVerifyOtp}>
                                <div className="qq-otp-row" onPaste={handleOtpPaste}>
                                    {otp.map((digit, i) => (
                                        <input
                                            key={i}
                                            ref={(el) => (otpInputRefs.current[i] = el)}
                                            value={digit}
                                            onChange={(e) => handleOtpChange(i, e.target.value)}
                                            onKeyDown={(e) => handleOtpKeyDown(i, e)}
                                            inputMode="numeric"
                                            maxLength={1}
                                            disabled={otpRemainingSeconds <= 0}
                                            className="qq-otp-box"
                                        />
                                    ))}
                                </div>

                                <div className="qq-otp-meta">
                                    {otpRemainingSeconds > 0 ? (
                                        <span>Code expires in <strong>{formatCountdown(otpRemainingSeconds)}</strong></span>
                                    ) : (
                                        <span style={{ color: "#e55353", fontWeight: 600 }}>Code expired</span>
                                    )}
                                </div>

                                <CButton
                                    type="submit"
                                    className="qq-submit-btn"
                                    disabled={loading || otpRemainingSeconds <= 0}
                                >
                                    {loading ? "Verifying..." : "Verify"}
                                </CButton>

                                <div className="qq-otp-meta">
                                    Didn't get the code?{" "}
                                    {resendCooldown > 0 ? (
                                        <span className="qq-otp-muted">Resend in {formatCountdown(resendCooldown)}</span>
                                    ) : (
                                        <span className="qq-otp-link" onClick={handleResendOtp}>
                                            {resendLoading ? "Sending..." : "Resend code"}
                                        </span>
                                    )}
                                </div>

                                <div className="qq-otp-meta">
                                    Wrong details?{" "}
                                    <span className="qq-otp-link" onClick={goBackToForm}>Back to details</span>
                                </div>
                            </CForm>
                        </>
                    )}

                    {step === STEP_DONE && (
                        <div className="text-center">
                            <h2 className="qq-auth-title">You're all set!</h2>
                            <CAlert color="success" className="mt-3">{successMessage}</CAlert>
                            <p className="qq-auth-subtitle">Redirecting…</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RegisterNew;